// SCR-STAFF-704 (AP-INT-05؛ US-MED-056، AC-MED-056.1): نمایشگر DICOM تحت وب با ابزارهای پایه — بزرگ‌نمایی و جابه‌جایی،
// کنتراست (پنجره‌سازی) و اندازه‌گیری. فریم‌ها **سمت سرور** رندر می‌شوند (WADO-RS `/rendered` یا رندر فایل DICOM سند)،
// پس مرورگر داده پیکسلی ۱۶ بیتی و رمزگشایی JPEG فشرده لازم ندارد (C-AGENT-1713).
// حریم خصوصی: تصویر فقط به‌صورت Object URL در حافظه صفحه است، پاسخ سرور no-store است و هر مشاهده در سرور Audit می‌شود.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { unwrap, type MedImagingInstanceView, type MedImagingStudyView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, LoadingState, PageHeader, toJalali } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useParams } from 'react-router';
import { decimal, labExchangeKeys, SCR_STAFF_704, useImagingFrame } from './labExchange';

type Tool = 'pan' | 'measure';

interface Point {
  x: number;
  y: number;
}

/** شمار فریم (قرارداد عدد را گاهی رشته می‌دهد). */
function frameCount(instance: MedImagingInstanceView): number {
  const parsed = Number(instance.frames);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/** پنجره پیش‌فرض نمایشگر (مقدار تهی = پنجره خود فایل یا کشش کامل بازه). */
const DEFAULT_WINDOW = { center: null as number | null, width: null as number | null };

export function ImagingViewerPage() {
  const { t } = useTranslation('med');
  const { resultId = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const tenantId = session?.tenantId;
  const [studyUid, setStudyUid] = useState<string | null>(null);
  const [instanceKey, setInstanceKey] = useState<string | null>(null);
  const [frame, setFrame] = useState(1);
  const [level, setLevel] = useState(DEFAULT_WINDOW);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [tool, setTool] = useState<Tool>('pan');
  const [measure, setMeasure] = useState<{ from: Point; to: Point } | null>(null);
  const dragging = useRef<{ kind: Tool; origin: Point; offset: Point } | null>(null);
  const surface = useRef<HTMLDivElement | null>(null);

  const studies = useQuery({
    queryKey: labExchangeKeys.studies(tenantId, resultId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/medical-orders/results/{resultId}/imaging-studies', { params: { path: { resultId } } })),
  });

  const study: MedImagingStudyView | null =
    (studies.data?.items ?? []).find((item) => item.studyInstanceUid === studyUid) ?? studies.data?.items?.[0] ?? null;

  const series = useQuery({
    queryKey: labExchangeKeys.series(tenantId, resultId, study?.studyInstanceUid ?? 'none'),
    enabled: Boolean(study),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/medical-orders/results/{resultId}/imaging-studies/{studyInstanceUid}/series', {
          params: { path: { resultId, studyInstanceUid: study!.studyInstanceUid } },
        }),
      ),
  });

  const instances = useMemo(
    () =>
      (series.data?.series ?? []).flatMap((row) =>
        row.instances.map((instance) => ({ seriesInstanceUid: row.seriesInstanceUid, modality: row.modality, instance })),
      ),
    [series.data],
  );
  const selected = instances.find((row) => `${row.seriesInstanceUid}|${row.instance.sopInstanceUid}` === instanceKey) ?? instances[0] ?? null;
  const spacing = decimal(selected?.instance.pixelSpacingMm);

  const picture = useImagingFrame(
    study
      ? {
          resultId,
          studyInstanceUid: study.studyInstanceUid,
          seriesInstanceUid: selected?.seriesInstanceUid ?? null,
          sopInstanceUid: selected?.instance.sopInstanceUid ?? null,
          frame,
          windowCenter: level.center,
          windowWidth: level.width,
        }
      : null,
  );

  const length = measure
    ? Math.hypot(measure.to.x - measure.from.x, measure.to.y - measure.from.y) / Math.max(zoom, 0.01)
    : null;

  const relative = (event: ReactPointerEvent<HTMLDivElement>): Point => {
    const bounds = surface.current?.getBoundingClientRect();
    return { x: event.clientX - (bounds?.left ?? 0), y: event.clientY - (bounds?.top ?? 0) };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const point = relative(event);
    if (tool === 'measure') {
      setMeasure({ from: point, to: point });
      dragging.current = { kind: 'measure', origin: point, offset };
      return;
    }
    dragging.current = { kind: 'pan', origin: point, offset };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragging.current;
    if (!drag) {
      return;
    }
    const point = relative(event);
    if (drag.kind === 'measure') {
      setMeasure((current) => (current ? { from: current.from, to: point } : current));
      return;
    }
    setOffset({ x: drag.offset.x + (point.x - drag.origin.x), y: drag.offset.y + (point.y - drag.origin.y) });
  };

  const onPointerUp = () => {
    dragging.current = null;
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setLevel(DEFAULT_WINDOW);
    setMeasure(null);
  };

  if (studies.isLoading) {
    return <LoadingState />;
  }

  if (studies.error) {
    return <ErrorState error={studies.error} onRetry={() => void studies.refetch()} />;
  }

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_704}>
      <PageHeader
        titleKey="med:imaging.title"
        subtitleKey="med:imaging.subtitle"
        badges={
          study ? (
            <Stack direction="row" spacing={0.5}>
              <Chip size="small" label={t(`med:imaging_source.${study.source}`, { defaultValue: study.source })} data-testid="med-imaging-source" />
              {study.isSample ? <Chip size="small" color="warning" label={t('med:imaging.sample')} data-testid="med-imaging-sample" /> : null}
            </Stack>
          ) : undefined
        }
      />

      {studies.data?.unavailable ? (
        <Alert severity="warning" data-testid="med-imaging-unavailable">
          {t('med:imaging.pacs_unavailable', { reason: studies.data.reason ?? '' })}
        </Alert>
      ) : null}

      {!study ? (
        <Alert severity="info" data-testid="med-imaging-empty">
          {t('med:imaging.no_study')}
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-end' }}>
            <TextField
              select
              size="small"
              label={t('med:imaging.study')}
              value={study.studyInstanceUid}
              onChange={(event) => {
                setStudyUid(event.target.value);
                setInstanceKey(null);
                setFrame(1);
                reset();
              }}
              data-testid="med-imaging-study"
              sx={{ minWidth: 260 }}
            >
              {(studies.data?.items ?? []).map((item) => (
                <MenuItem key={item.studyInstanceUid} value={item.studyInstanceUid}>
                  {item.description ?? item.accessionNumber ?? item.studyInstanceUid}
                  {item.studyDate ? ` — ${toJalali(item.studyDate)}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('med:imaging.image')}
              value={selected ? `${selected.seriesInstanceUid}|${selected.instance.sopInstanceUid}` : ''}
              onChange={(event) => {
                setInstanceKey(event.target.value);
                setFrame(1);
                setMeasure(null);
              }}
              disabled={instances.length === 0}
              data-testid="med-imaging-instance"
              sx={{ minWidth: 260 }}
            >
              {instances.map((row) => (
                <MenuItem key={`${row.seriesInstanceUid}|${row.instance.sopInstanceUid}`} value={`${row.seriesInstanceUid}|${row.instance.sopInstanceUid}`}>
                  {(row.modality ?? t('med:imaging.image')) + ' ' + faDigits(row.instance.instanceNumber ?? 1)}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tool}
              onChange={(_event, next: Tool | null) => next && setTool(next)}
              aria-label={t('med:imaging.tools')}
            >
              <ToggleButton value="pan" data-testid="med-imaging-tool-pan">
                {t('med:imaging.tool_pan')}
              </ToggleButton>
              <ToggleButton value="measure" data-testid="med-imaging-tool-measure">
                {t('med:imaging.tool_measure')}
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" onClick={reset} data-testid="med-imaging-reset">
              {t('med:imaging.reset')}
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Stack spacing={0.5} sx={{ minWidth: 200 }}>
              <Typography variant="caption">{t('med:imaging.zoom')}</Typography>
              <Slider
                value={zoom}
                min={0.5}
                max={6}
                step={0.1}
                onChange={(_event, next) => setZoom(next as number)}
                aria-label={t('med:imaging.zoom')}
                data-testid="med-imaging-zoom"
              />
            </Stack>
            <Stack spacing={0.5} sx={{ minWidth: 200 }}>
              <Typography variant="caption">{t('med:imaging.window_center')}</Typography>
              <Slider
                value={level.center ?? 0}
                min={-1024}
                max={3072}
                step={16}
                onChange={(_event, next) => setLevel((current) => ({ ...current, center: next as number }))}
                aria-label={t('med:imaging.window_center')}
                data-testid="med-imaging-window-center"
              />
            </Stack>
            <Stack spacing={0.5} sx={{ minWidth: 200 }}>
              <Typography variant="caption">{t('med:imaging.window_width')}</Typography>
              <Slider
                value={level.width ?? 400}
                min={1}
                max={4096}
                step={16}
                onChange={(_event, next) => setLevel((current) => ({ ...current, width: next as number }))}
                aria-label={t('med:imaging.window_width')}
                data-testid="med-imaging-window-width"
              />
            </Stack>
          </Stack>

          <Box
            ref={surface}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            data-testid="med-imaging-surface"
            sx={{
              position: 'relative',
              overflow: 'hidden',
              bgcolor: 'common.black',
              minHeight: 360,
              borderRadius: 1,
              cursor: tool === 'measure' ? 'crosshair' : 'grab',
              touchAction: 'none',
            }}
          >
            {picture.error ? (
              <Alert severity="warning" data-testid="med-imaging-frame-error" sx={{ m: 2 }}>
                {t('med:imaging.frame_unavailable')}
              </Alert>
            ) : picture.url ? (
              <Box
                component="img"
                src={picture.url}
                alt={t('med:imaging.alt')}
                draggable={false}
                data-testid="med-imaging-frame"
                sx={{
                  display: 'block',
                  margin: '0 auto',
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  userSelect: 'none',
                  maxWidth: '100%',
                }}
              />
            ) : (
              <LoadingState />
            )}
            {measure ? (
              <Box
                component="svg"
                sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                aria-hidden="true"
              >
                <line x1={measure.from.x} y1={measure.from.y} x2={measure.to.x} y2={measure.to.y} stroke="#4fc3f7" strokeWidth={2} />
              </Box>
            ) : null}
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            {length !== null ? (
              <Typography variant="body2" data-testid="med-imaging-measure">
                {spacing
                  ? t('med:imaging.length_mm', { value: faDigits((length * spacing).toFixed(1)) })
                  : t('med:imaging.length_px', { value: faDigits(Math.round(length)) })}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('med:imaging.measure_hint')}
              </Typography>
            )}
            {!spacing && length !== null ? (
              <Typography variant="caption" color="text.secondary" data-testid="med-imaging-no-spacing">
                {t('med:imaging.no_spacing')}
              </Typography>
            ) : null}
            {selected && frameCount(selected.instance) > 1 ? (
              <FrameStepper instance={selected.instance} frame={frame} onChange={setFrame} />
            ) : null}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

/** گام فریم در مطالعه چندفریمی (سینه‌لوپ یا برش‌ها). */
function FrameStepper({ instance, frame, onChange }: { instance: MedImagingInstanceView; frame: number; onChange: (value: number) => void }) {
  const { t } = useTranslation('med');
  const total = frameCount(instance);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption">{t('med:imaging.frame', { value: faDigits(frame), total: faDigits(total) })}</Typography>
      <Button size="small" disabled={frame <= 1} onClick={() => onChange(frame - 1)} data-testid="med-imaging-frame-prev">
        {t('med:imaging.previous')}
      </Button>
      <Button size="small" disabled={frame >= total} onClick={() => onChange(frame + 1)} data-testid="med-imaging-frame-next">
        {t('med:imaging.next')}
      </Button>
    </Stack>
  );
}
